import { useEffect, useState } from "react";
import { Swords, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Challenge() {
  const [games, setGames] = useState([]);
  const [form, setForm] = useState({ challenger_team: "", challenger_email: "", challenged_name: "", challenged_email: "", game_id: "", message: "" });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/games").then((r) => setGames(r.data)); }, []);

  const submit = async () => {
    if (!form.game_id) return toast.error("Pick a game");
    try {
      const { data } = await api.post("/challenges", form);
      setResult(data);
      toast.success("Challenge sent for approval!");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const link = result ? `${window.location.origin}/challenge/${result.invite_code}` : "";

  return (
    <div className="max-w-2xl mx-auto px-5 py-14">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-violet-300 font-mono text-xs uppercase tracking-wider mb-2">
          <Swords className="h-4 w-4" /> Team vs Team
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Throw down a challenge</h1>
        <p className="text-slate-400 mt-3">Dare another company or department to beat your escape time. We'll email them an invite once our crew approves it.</p>
      </div>

      {!result ? (
        <div className="rounded-2xl border border-white/10 bg-[#131a29] p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your team name"><Input data-testid="ch-team" value={form.challenger_team} onChange={(e) => setForm({ ...form, challenger_team: e.target.value })} className="bg-slate-950/50 border-white/10" /></Field>
            <Field label="Your email"><Input data-testid="ch-email" type="email" value={form.challenger_email} onChange={(e) => setForm({ ...form, challenger_email: e.target.value })} className="bg-slate-950/50 border-white/10" /></Field>
            <Field label="Rival team / person"><Input data-testid="ch-rival-name" value={form.challenged_name} onChange={(e) => setForm({ ...form, challenged_name: e.target.value })} className="bg-slate-950/50 border-white/10" /></Field>
            <Field label="Rival email"><Input data-testid="ch-rival-email" type="email" value={form.challenged_email} onChange={(e) => setForm({ ...form, challenged_email: e.target.value })} className="bg-slate-950/50 border-white/10" /></Field>
          </div>
          <Field label="Game">
            <Select value={form.game_id} onValueChange={(v) => setForm({ ...form, game_id: v })}>
              <SelectTrigger data-testid="ch-game" className="bg-slate-950/50 border-white/10"><SelectValue placeholder="Choose a game" /></SelectTrigger>
              <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Trash talk (optional)"><Textarea data-testid="ch-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-950/50 border-white/10" placeholder="You'll never beat 21:24…" /></Field>
          <Button data-testid="ch-submit" onClick={submit} className="w-full bg-violet-600 hover:bg-violet-500"><Send className="h-4 w-4 mr-2" /> Send challenge</Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8 text-center">
          <div className="font-display font-bold text-2xl mb-2">Challenge created! ⚡</div>
          <p className="text-slate-400">Pending crew approval. Once approved, {form.challenged_name} gets an email. Share this link too:</p>
          <div className="mt-4 flex items-center gap-2 bg-slate-950/60 border border-white/10 rounded-xl p-3">
            <span className="font-mono text-sm text-violet-300 truncate flex-1 text-left">{link}</span>
            <button onClick={() => { navigator.clipboard.writeText(link); setCopied(true); }} data-testid="copy-link" className="p-2 rounded-lg hover:bg-white/10">
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-3 font-mono text-lg tracking-widest text-amber-400">Code: {result.invite_code}</div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-sm text-slate-400 mb-1.5 block">{label}</label>{children}</div>
);
